import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  QuestionType,
  FollowUpAnswer,
  MultipleChoiceAnswer,
  LikertScaleAnswer,
  OpenEndedAnswer,
  SurveyQuestion,
  BaseAnswer,
  SurveyResponseAnswer,
} from '@x-guard/xgac-types/xgac';

import { useLocale } from 'src/lib/hook';
import styles from './style.module.scss';
import TextInputBox from './TextInputBox';
import FollowUp from './FollowUp';
import LikertScale from './LikertScale';
import MultipleChoice from './MultipleChoice';

interface AnswerTypeProps {
  question: SurveyQuestion;
  nextQuestion: (answer: SurveyResponseAnswer, isCompleted?: boolean, skipResponse?: boolean) => void;
}

export interface SelectedOption {
  value: number;
  label: string;
}

export const AnswerType: React.FC<AnswerTypeProps> = ({ question, nextQuestion }) => {

  const [inputValue, setInputValue] = useState('');

  const [selectedFollowUpOptions, setSelectedFollowUpOptions] = useState<SelectedOption[]>([]);
  const [visible, setVisible] = useState(true);
  const [animate, setAnimate] = useState(false);
  const { t } = useLocale();

  useEffect(() => {

    setAnimate(true);
    const timer = setTimeout(() => setAnimate(false), 500);
    return () => clearTimeout(timer);

  }, [question.type]);

  const handleMultipleChoiceClick = (answer: BaseAnswer) => {

    const response: MultipleChoiceAnswer = {
      type: QuestionType.MultipleChoice,
      answer,
      question: { _id: question._id, _ref: 'SurveyQuestion' },
    };
    nextQuestion(response);

  };

  const handleFollowUpContinueButtonClick = (selectedOptions: SelectedOption[]) => {

    setSelectedFollowUpOptions(selectedOptions);

    const response: FollowUpAnswer = ({
      type: QuestionType.FollowUp,
      question: { _id: question._id, _ref: 'SurveyQuestion' },
      answer: selectedOptions,
    });

    nextQuestion(response);

  };

  const handleLikertScaleClick = (answer: BaseAnswer) => {

    const response: LikertScaleAnswer = {
      type: QuestionType.LikertScale,
      answer,
      question: { _id: question._id, _ref: 'SurveyQuestion' },
    };
    nextQuestion(response);

  };

  const handleSubmit = () => {

    const sanitizedInputValue = DOMPurify.sanitize(inputValue);

    const response: OpenEndedAnswer = {
      type: QuestionType.OpenEnded,
      answer: sanitizedInputValue,
      question: { _id: question._id, _ref: 'SurveyQuestion' },
    };

    nextQuestion(response, true, sanitizedInputValue.trim().length === 0);

  };

  useEffect(() => {

    setVisible(true);

  }, [question.type]);

  const renderComponent = () => {

    if (!visible) return null;

    const options = question.options || [];

    switch (question.type) {

    case QuestionType.MultipleChoice:
      return <MultipleChoice options={options} onClick={handleMultipleChoiceClick} />;
    case QuestionType.LikertScale:
      return <LikertScale onClick={handleLikertScaleClick} options={options} />;
    case QuestionType.FollowUp:
      return (
        <div className={styles['follow-up']}>
          <FollowUp options={options} selectedFollowUpOptions={selectedFollowUpOptions} onContinuePress={handleFollowUpContinueButtonClick} />
        </div>
      );
    case QuestionType.OpenEnded:
      return (
        <div className={styles['open-ended']}>
          <TextInputBox
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            handleSubmit={handleSubmit}
            placeholder={t('component.textInputBox')}
          />
        </div>
      );
    default:
      return <div>Default Component</div>;

    }

  };

  return (
    <div className={`${styles['answer-type-container']} ${animate ? styles['fade-in'] : ''}`}>
      {renderComponent()}
    </div>
  );

};
